// @flow

// Add polyfills
import 'whatwg-fetch'; // fetch
import "@webcomponents/custom-elements"; // window.customElements

import './public-path';

import React from 'react';
import Relay from 'react-relay/classic';
import reactRender from './lib/reactRenderer';
import Routes from './routes';
import EmojiStyleManager from './lib/EmojiStyleManager';
import Environment from 'app/lib/relay/environment';

// These are custom web components that just need loaded so HTML on the page
// can use them
import "@github/details-menu-element";
import "@github/include-fragment-element";
import "./web-components/ReactElement";
import "./web-components/TimeWithFormatElement";
import "./web-components/NumberWithFormatElement";

// Create the a Relay Modern environment
Environment.create();

// Detect and adjust for custom emoji scaling
EmojiStyleManager.apply();

require("app/css");

// Toggle on development features
if (process.env.NODE_ENV === "development") {
  require('react-type-snob').default(React);
  require('./lib/Logger').default.enable();
  require('react-relay/lib/RelayNetworkDebug').init();
}

// Allows old sprockets and inline-javascript to access webpack modules
window["require"] = function(module: string) {
  const exported = window["require"].modules[module];

  if (exported) {
    return exported();
  }

  throw "No webpack module exported `" + module + "`";
};

const modules: { [string]: () => mixed } = {
  "@rails/ujs": () => require("@rails/ujs"),
  "autosize": () => require("autosize"),
  "credit-card-type": () => require("credit-card-type"),
  "react": () => require("react"),
  "react-dom": () => require("react-dom"),
  "react-relay": () => require("react-relay/classic"),

  // "shared" components can be referred to by name from Rails views
  // see `app/components/react_component.rb` for details
  "app/components/shared/BetaBadge": () => require("./components/shared/BetaBadge").default,
  "app/components/shared/Chart": () => require("./components/shared/Chart").default,
  "app/components/shared/Dialog": () => require("./components/shared/Dialog").default,
  "app/components/shared/FormMarkdownEditorField": () => require("./components/shared/FormMarkdownEditorField").default,
  "app/components/shared/FormYAMLEditorField": () => require("./components/shared/FormYAMLEditorField").default,
  "app/components/shared/Icon": () => require("./components/shared/Icon").default,
  "app/components/shared/ImageUploadField": () => require("./components/shared/ImageUploadField").default,
  "app/components/shared/Spinner": () => require("./components/shared/Spinner").default,
  "app/components/icons/BuildState.Small": () => require("./components/icons/BuildState").default.Small,
  "app/components/BuildState": () => require("./components/BuildState").default,

  "app/components/AnonymousNavigation": () => require("./components/AnonymousNavigation").default,
  "app/components/billing/InvoiceNoteModal": () => require("./components/billing/InvoiceNoteModal").default,
  "app/components/build/List": () => require("./components/build/List").default,
  "app/components/build/Show": () => require("./components/build/Show").default,
  "app/components/build/Show/Annotation": () => require("./components/build/Show/Annotation").default,
  "app/components/build/StateSwitcher": () => require("./components/build/StateSwitcher").default,
  "app/components/ClusterQueueAgents": () => require("./components/ClusterQueueAgents").default,
  "app/components/layout/Flashes": () => require("./components/layout/Flashes").default,
  "app/components/layout/Footer": () => require("./components/layout/Footer").default,
  "app/components/layout/Navigation": () => require("./components/layout/Navigation").default,
  "app/components/pipeline/BranchList": () => require("./components/pipeline/BranchList").default,
  "app/components/pipeline/Edit/Pipeline": () => require("./components/pipeline/Edit/Pipeline").default,
  "app/components/pipeline/Header": () => require("./components/pipeline/Header").default,
  "app/components/user/graphql/schema": () => require("./components/user/graphql/schema"),
  "app/components/user/PipelineEmailSettings": () => require("./components/user/PipelineEmailSettings").default,

  "app/css/style-guide": () => require("./css/style-guide").default,

  "app/lib/CreditCardManager": () => require('./lib/CreditCardManager').default,
  "app/lib/legacyDispatcher": () => require("./lib/legacyDispatcher").default,
  "app/lib/Logger": () => require("./lib/Logger").default,
  "app/lib/reactRenderer": () => require("./lib/reactRenderer").default,
  "app/lib/RelayModernPreloader": () => require("./lib/RelayModernPreloader").default,
  "app/lib/RelayPreloader": () => require("./lib/RelayPreloader").default,
  "app/lib/TabManager": () => require("./lib/TabManager").default,
  "app/lib/Websocket": () => require("./lib/Websocket").default,

  "app/queries/Build": () => require("./queries/Build"),
  "app/queries/Organization": () => require("./queries/Organization"),
  "app/queries/Pipeline": () => require("./queries/Pipeline"),
  "app/queries/Viewer": () => require("./queries/Viewer"),

  "app/stores/BranchStore": () => require("./stores/BranchStore").default,
  "app/stores/BuildPageStore": () => require("./stores/BuildPageStore").default,
  "app/stores/BuildShowStore": () => require('./stores/BuildShowStore').default,
  "app/stores/BuildStore": () => require("./stores/BuildStore").default,
  "app/stores/CableStore": () => require("./stores/CableStore").default,
  "app/stores/FlashesStore": () => require("./stores/FlashesStore").default,
  "app/stores/JobInformationStore": () => require("./stores/JobInformationStore").default,
  "app/stores/JobLogStore": () => require("./stores/JobLogStore").default,
  "app/stores/ProjectPipelineStore": () => require("./stores/ProjectPipelineStore").default,
  "app/stores/UserStore": () => require("./stores/UserStore").default
};

window["require"].modules = modules;

// Configure relay if we have access to the GraphQL URL
if (window._graphql) {
  Relay.injectNetworkLayer(
    new Relay.DefaultNetworkLayer(
      window._graphql["url"],
      {
        credentials: "same-origin",
        headers: window._graphql["headers"],
        // Standard relay values, that can be overriden by some pages. If a
        // request is taking a while, we don't want to force Relay to make
        // another because that's just gonna slow down the server even more. So
        // we'll bump the default fetchTimeout to 10m
        fetchTimeout: 600000, // 10m
        retryDelays: window._graphql["retryDelays"] || [1000, 3000]
      }
    )
  );
}

// Set up Action Cable, if configured
if (window._cable) {
  window._cable.consumer = require("@rails/actioncable").createConsumer();

  window._cable.pingChannel = require("./channels/PingChannel").default.subscribe({}, {
    received: (data) => window.console.log("[PingChannel]", data)
  });

  if (window._cable.channels) {
    const cableStore = require("./stores/CableStore").default;

    for (const channel of window._cable["channels"]) {
      if (typeof channel == "string") {
        cableStore.listen({ channel: channel });
      } else {
        cableStore.listen(channel);
      }
    }
  }
}

// Only do the react-router gear on pages we've designated
window["initializeReactRouter"] = () => {
  const rootElement = document && document.getElementById("root");

  if (!rootElement) {
    throw new Error("No #root element to render the application to!");
  }

  reactRender(Routes, rootElement);
};
